import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebaseConfig';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  MenuItem,
  Button,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import BarChart from '../components/BarChart';
import {
  exportCsv,
  exportXlsx,
  exportXlsxSheets,
  exportCsvSections,
  exportPdf,
  svgToPng,
  ChartImage,
} from '../utils/exporters';

/**
 * Reports section (Phase 1: Revenue and Sessions).
 *
 * A single page with a shared filter bar — pick a report, a partner, one of
 * that partner's locations, a period grouping, and a date range, then Run.
 *
 * It only READS Firestore (payments, parkingsessions, parkinglots, vendors);
 * nothing here writes, so it cannot affect live data. Filtering is done in the
 * browser after a scoped query, which is fine at the current data size. At
 * larger scale the heavy reports should move to indexed queries or server-side
 * aggregation (Cloud Functions) — see the note in the header comment.
 */

// ---- reference data shapes -------------------------------------------------
interface Partner {
  id: string; // join key: the partner's vendorId FIELD (what lots/payments carry), not the doc id
  docId: string; // Firestore document id — used only as a stable React key
  name: string;
}
interface Lot {
  id: string;
  name: string;
  code: string;
  vendorId: string;
}

// ---- helpers ---------------------------------------------------------------

/** Firestore timestamps come as Timestamp objects, {seconds}, or (legacy) as
 *  strings. Parse all of them, returning null when unusable. */
function parseDate(v: any): Date | null {
  if (v === null || v === undefined) return null;
  if (typeof v?.toDate === 'function') return v.toDate();
  if (typeof v?.seconds === 'number') return new Date(v.seconds * 1000);
  if (typeof v === 'string' || typeof v === 'number') {
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

/** Group key for a date at the chosen granularity. */
function periodKey(d: Date, groupBy: string): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  if (groupBy === 'year') return `${y}`;
  if (groupBy === 'month') return `${y}-${m}`;
  if (groupBy === 'week') {
    // ISO week number.
    const t = new Date(Date.UTC(y, d.getMonth(), d.getDate()));
    const dayNum = (t.getUTCDay() + 6) % 7;
    t.setUTCDate(t.getUTCDate() - dayNum + 3);
    const firstThursday = new Date(Date.UTC(t.getUTCFullYear(), 0, 4));
    const week =
      1 +
      Math.round(
        ((t.getTime() - firstThursday.getTime()) / 86400000 -
          3 +
          ((firstThursday.getUTCDay() + 6) % 7)) /
          7
      );
    return `${t.getUTCFullYear()}-W${String(week).padStart(2, '0')}`;
  }
  return `${y}-${m}-${day}`;
}

function money(n: number): string {
  return `$${(Number(n) || 0).toFixed(2)}`;
}

/** Build a CSV and trigger a download — no library needed. */
function downloadCsv(filename: string, headers: string[], rows: (string | number)[][]): void {
  const esc = (s: any) => `"${String(s ?? '').replace(/"/g, '""')}"`;
  const csv = [headers.map(esc).join(','), ...rows.map((r) => r.map(esc).join(','))].join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}
function isoToday(): string {
  return isoLocal(new Date());
}

/** Local YYYY-MM-DD (not UTC), so the date buttons land on the right day. */
function isoLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

type Preset = 'today' | 'week' | 'month' | 'year' | 'last30' | 'last90' | 'all';

/** A quick range returns { from, to } dates plus a sensible grouping. */
function presetRange(p: Preset): { from: string; to: string; groupBy: 'day' | 'week' | 'month' | 'year' } {
  const now = new Date();
  const to = isoLocal(now);
  if (p === 'today') return { from: to, to, groupBy: 'day' };
  if (p === 'week') {
    const d = new Date(now);
    const dow = (d.getDay() + 6) % 7; // Monday = 0
    d.setDate(d.getDate() - dow);
    return { from: isoLocal(d), to, groupBy: 'day' };
  }
  if (p === 'month') return { from: isoLocal(new Date(now.getFullYear(), now.getMonth(), 1)), to, groupBy: 'day' };
  if (p === 'year') return { from: isoLocal(new Date(now.getFullYear(), 0, 1)), to, groupBy: 'month' };
  if (p === 'last30') {
    const d = new Date(now); d.setDate(d.getDate() - 30);
    return { from: isoLocal(d), to, groupBy: 'day' };
  }
  if (p === 'last90') {
    const d = new Date(now); d.setDate(d.getDate() - 90);
    return { from: isoLocal(d), to, groupBy: 'week' };
  }
  return { from: '2000-01-01', to, groupBy: 'month' }; // all time
}

const PRESETS: { key: Preset; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'This week' },
  { key: 'month', label: 'This month' },
  { key: 'year', label: 'This year' },
  { key: 'last30', label: 'Last 30 days' },
  { key: 'last90', label: 'Last 90 days' },
  { key: 'all', label: 'All time' },
];

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <Card sx={{ flex: '1 1 220px', minWidth: 200 }}>
      <CardContent>
        <Typography color="textSecondary" gutterBottom>
          {label}
        </Typography>
        <Typography variant="h4">{value}</Typography>
      </CardContent>
    </Card>
  );
}

// ---- result shapes ---------------------------------------------------------
interface PeriodRow {
  period: string;
  count: number;
  total: number;
}
interface SessionRow {
  when: Date | null;
  whenLabel: string;
  partner: string;
  lot: string;
  customer: string;
  vehicle: string;
  minutes: number;
  amount: number;
  method: string;
  status: string;
}
interface CardRow {
  when: Date | null;
  whenLabel: string;
  partner: string;
  lot: string;
  customer: string;
  method: string; // "Credit Card" / "Credit Card (Extension)"
  gateway: string; // nmi / stripe
  txnId: string;
  parkingFee: number;
  salesTax: number;
  processingFee: number;
  amount: number;
  currency: string;
}

// Grouped model: every report is broken out Partner -> Location -> period, so
// partners are never mixed together and each location stands on its own.
interface LocGroup {
  lotName: string;
  periods: PeriodRow[]; // day/week/month/year buckets for THIS location only
  count: number; // sessions / payments at this location
  total: number; // revenue at this location
  detail: any[]; // SessionRow[] or CardRow[] (empty for the revenue report)
}
interface PartnerGroup {
  partnerName: string;
  locations: LocGroup[];
  count: number;
  total: number;
}

// Turn a flat list of records into the Partner -> Location -> period tree.
function buildGroups(
  recs: { when: Date; partnerName: string; lotName: string; amount: number; detail?: any }[],
  groupBy: string
): { groups: PartnerGroup[]; total: number; count: number } {
  const pmap: Record<string, PartnerGroup> = {};
  let total = 0;
  let count = 0;
  recs.forEach((r) => {
    const pn = r.partnerName || '—';
    const ln = r.lotName || '—';
    let pg = pmap[pn];
    if (!pg) {
      pg = { partnerName: pn, locations: [], count: 0, total: 0 };
      pmap[pn] = pg;
    }
    let lg = pg.locations.find((l) => l.lotName === ln);
    if (!lg) {
      lg = { lotName: ln, periods: [], count: 0, total: 0, detail: [] };
      pg.locations.push(lg);
    }
    const key = periodKey(r.when, groupBy);
    let per = lg.periods.find((p) => p.period === key);
    if (!per) {
      per = { period: key, count: 0, total: 0 };
      lg.periods.push(per);
    }
    per.count += 1;
    per.total += r.amount;
    lg.count += 1;
    lg.total += r.amount;
    pg.count += 1;
    pg.total += r.amount;
    total += r.amount;
    count += 1;
    if (r.detail) lg.detail.push(r.detail);
  });
  const groups = Object.values(pmap).sort((a, b) => a.partnerName.localeCompare(b.partnerName));
  groups.forEach((pg) => {
    pg.locations.sort((a, b) => a.lotName.localeCompare(b.lotName));
    pg.locations.forEach((lg) => {
      lg.periods.sort((a, b) => a.period.localeCompare(b.period));
      lg.detail.sort((a: any, b: any) => (b.when?.getTime() || 0) - (a.when?.getTime() || 0));
    });
  });
  return { groups, total, count };
}

interface ReportsProps {
  /** Lock this page to a single report and hide the report picker. */
  fixedType?: 'revenue' | 'sessions' | 'cards';
  /** Scope every query to this partner and hide the partner picker
   *  (used by the vendor/partner dashboard so a partner sees only their own). */
  lockedVendorId?: string;
}

export default function Reports({ fixedType, lockedVendorId }: ReportsProps = {}) {
  // reference data
  const [partners, setPartners] = useState<Partner[]>([]);
  const [lots, setLots] = useState<Lot[]>([]);
  const [refLoading, setRefLoading] = useState(true);

  // filters
  const [reportType, setReportType] = useState<'revenue' | 'sessions' | 'cards'>(fixedType ?? 'revenue');
  const [partnerId, setPartnerId] = useState(lockedVendorId ?? 'all');
  const [lotId, setLotId] = useState('all');
  const [groupBy, setGroupBy] = useState<'day' | 'week' | 'month' | 'year'>('day');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'closed'>('all');
  const [gatewayFilter, setGatewayFilter] = useState<'all' | 'nmi' | 'stripe'>('all');
  const [fromDate, setFromDate] = useState(isoDaysAgo(30));
  const [toDate, setToDate] = useState(isoToday());

  // results
  const [running, setRunning] = useState(false);
  const [hasRun, setHasRun] = useState(false);
  const [groups, setGroups] = useState<PartnerGroup[]>([]);
  const [grandTotal, setGrandTotal] = useState(0);
  const [grandCount, setGrandCount] = useState(0);

  // Load reference data. Lots and vendors are fetched independently because a
  // partner login can read parkinglots (rules allow any signed-in user) but
  // NOT the vendors collection (admin-only) — so one must not block the other.
  useEffect(() => {
    (async () => {
      setRefLoading(true);

      // Lots — readable by admin and partner alike.
      try {
        const lSnap = await getDocs(collection(db, 'parkinglots'));
        setLots(
          lSnap.docs
            .map((d) => {
              const data = d.data() as any;
              return {
                id: d.id,
                name: data.lotName || d.id,
                code: data.lotCode || '',
                vendorId: data.vendorId || '',
              };
            })
            .sort((a, b) => a.name.localeCompare(b.name))
        );
      } catch (e) {
        console.error('Reports: failed to load lots', e);
      }

      // Vendors — admin-only. Skip for a locked partner (they can't read it and
      // don't need the partner picker anyway).
      if (!lockedVendorId) {
        try {
          const vSnap = await getDocs(collection(db, 'vendors'));
          setPartners(
            vSnap.docs
              .map((d) => {
                const data = d.data() as any;
                // Partners link to lots and payments by their vendorId FIELD,
                // not the document id. Older partners carry a "vendor_<timestamp>"
                // value; self-signup partners carry their uid (= the doc id).
                return {
                  id: data.vendorId || d.id,
                  docId: d.id,
                  name: data.companyName || data.name || d.id,
                };
              })
              .sort((a, b) => a.name.localeCompare(b.name))
          );
        } catch (e) {
          console.error('Reports: failed to load vendors', e);
        }
      }

      setRefLoading(false);
    })();
  }, [lockedVendorId]);

  // Lots shown in the Location dropdown depend on the chosen partner.
  const lotsForPartner = useMemo(
    () => (partnerId === 'all' ? lots : lots.filter((l) => l.vendorId === partnerId)),
    [lots, partnerId]
  );
  const lotById = useMemo(() => {
    const m: Record<string, Lot> = {};
    lots.forEach((l) => (m[l.id] = l));
    return m;
  }, [lots]);
  const partnerById = useMemo(() => {
    const m: Record<string, string> = {};
    partners.forEach((p) => (m[p.id] = p.name));
    return m;
  }, [partners]);

  // Reset the location when the partner changes so a stale lot can't linger.
  useEffect(() => {
    setLotId('all');
  }, [partnerId]);

  // Keep in sync when mounted as a fixed/locked page.
  useEffect(() => {
    if (fixedType) setReportType(fixedType);
  }, [fixedType]);
  useEffect(() => {
    if (lockedVendorId) setPartnerId(lockedVendorId);
  }, [lockedVendorId]);

  function dateWindow(): { start: Date; end: Date } {
    const start = new Date(fromDate + 'T00:00:00');
    const end = new Date(toDate + 'T23:59:59.999');
    return { start, end };
  }

  function applyPreset(p: Preset) {
    const r = presetRange(p);
    setFromDate(r.from);
    setToDate(r.to);
    setGroupBy(r.groupBy);
  }

  // Partner (locked) mode fetches through the getPartnerReport Cloud Function,
  // which is the only way a partner can read their own payment/session data
  // (client security rules keep those collections admin-only).
  //
  // CHANGED 2026-08-28: the Revenue report now uses this in ADMIN mode too.
  // Reading Firestore directly returns app payments only, so the admin view was
  // silently missing every penny of ONSITE gate/kiosk money — which arrives from
  // ARCS Central and only this function fetches. A partner saw their true total
  // and ACG did not, which is backwards. The function already accepts an explicit
  // vendorId from an admin caller, so nothing changed server-side.
  async function callReport(
    report: 'revenue' | 'cards' | 'sessions',
    vendorId: string | undefined = lockedVendorId,
  ): Promise<any[]> {
    const { start, end } = dateWindow();
    const fn = httpsCallable(functions, 'getPartnerReport');
    const res: any = await fn({
      report,
      fromMs: start.getTime(),
      toMs: end.getTime(),
      // Added 2026-08-28. The onsite half is fetched from ARCS Central, which
      // filters on the SITE's own calendar dates. Deriving those from the
      // millisecond window meant a UTC conversion of a local-midnight boundary,
      // which pulled in an extra day. Sending the picked dates removes the
      // conversion entirely.
      fromDate,
      toDate,
      vendorId,
    });
    return (res.data && res.data.rows) || [];
  }

  // Onsite rows come from ARCS Central already scoped to the chosen dates, and
  // their `ts` is approximate: the receipt carries the site's wall-clock with no
  // timezone, so it is parsed as UTC and lands hours out. Filtering those by the
  // millisecond window would drop early-morning payments off the front of a day.
  // Anything else (app payments, real Firestore timestamps) is filtered normally.
  function outsideWindow(r: any, start: Date, end: Date): boolean {
    if (r && r.source === 'onsite') return false;
    const when = new Date(r.ts);
    return when < start || when > end;
  }

  function applyGroups(recs: { when: Date; partnerName: string; lotName: string; amount: number; detail?: any }[]) {
    const { groups: g, total, count } = buildGroups(recs, groupBy);
    setGroups(g);
    setGrandTotal(total);
    setGrandCount(count);
  }

  async function runRevenue() {
    const { start, end } = dateWindow();
    const selectedLotName = lotId === 'all' ? null : lotById[lotId]?.name ?? null;
    const recs: { when: Date; partnerName: string; lotName: string; amount: number }[] = [];

    if (lockedVendorId) {
      const rows = await callReport('revenue');
      const pname = partnerById[lockedVendorId] || 'Your site';
      rows.forEach((r: any) => {
        const when = new Date(r.ts);
        if (outsideWindow(r, start, end)) return;
        if (selectedLotName && (r.lotName || '') !== selectedLotName) return;
        recs.push({ when, partnerName: pname, lotName: r.lotName || '—', amount: Number(r.amount) || 0 });
      });
      applyGroups(recs);
      return;
    }

    // ADMIN MODE. Changed 2026-08-28.
    //
    // This used to read the `payments` collection straight from Firestore. That
    // returns app payments only, so the admin Revenue report was missing all
    // ONSITE gate/kiosk money — the partner's own view showed a bigger, truer
    // number than ACG's did.
    //
    // Onsite revenue lives in ARCS Central and reaches us only through
    // getPartnerReport, which merges it per vendor. So call it once per partner
    // and concatenate. The function returns app payments AND onsite for that
    // vendor, already de-duplicated (Central deliberately returns onsite only,
    // because app payments are mirrored into Firestore), so this does not
    // double-count.
    //
    // Cost: one call per partner when "all" is selected, instead of one
    // Firestore read. There are a handful of partners today. If that grows
    // enough to hurt, give the function an "all partners" mode rather than
    // going back to reading Firestore, which cannot see onsite money at all.
    const vendorIds =
      partnerId === 'all' ? Object.keys(partnerById) : [partnerId];

    const perPartner = await Promise.all(
      vendorIds.map(async (vid) => {
        try {
          return { vid, rows: await callReport('revenue', vid) };
        } catch (e) {
          // One partner failing must not blank the whole report.
          console.error(`Revenue for partner ${vid} failed`, e);
          return { vid, rows: [] as any[] };
        }
      })
    );

    perPartner.forEach(({ vid, rows }) => {
      rows.forEach((r: any) => {
        const when = new Date(r.ts);
        if (outsideWindow(r, start, end)) return;
        if (selectedLotName && (r.lotName || '') !== selectedLotName) return;
        recs.push({
          when,
          partnerName: partnerById[vid] || vid || '—',
          lotName: r.lotName || '—',
          amount: Number(r.amount) || 0,
        });
      });
    });

    applyGroups(recs);
  }

  async function runSessions() {
    const { start, end } = dateWindow();
    const recs: { when: Date; partnerName: string; lotName: string; amount: number; detail: SessionRow }[] = [];

    if (lockedVendorId) {
      const selectedLotName = lotId === 'all' ? null : lotById[lotId]?.name ?? null;
      const rows = await callReport('sessions');
      const pname = partnerById[lockedVendorId] || 'Your site';
      rows.forEach((r: any) => {
        const when = new Date(r.ts);
        if (when < start || when > end) return;
        if (selectedLotName && (r.lotName || '') !== selectedLotName) return;
        const active = !!r.active;
        if (statusFilter === 'active' && !active) return;
        if (statusFilter === 'closed' && active) return;
        const amt = Number(r.amount) || 0;
        const lotName = r.lotName || '—';
        recs.push({
          when,
          partnerName: pname,
          lotName,
          amount: amt,
          detail: {
            when,
            whenLabel: when.toLocaleString(),
            partner: pname,
            lot: lotName,
            customer: r.customer || '—',
            vehicle: r.vehicle || '—',
            minutes: Number(r.minutes) || 0,
            amount: amt,
            method: r.gateway || '—',
            status: active ? 'Active' : 'Closed',
          },
        });
      });
      applyGroups(recs);
      return;
    }

    const base = collection(db, 'parkingsessions');
    const snap = await getDocs(
      statusFilter === 'active'
        ? query(base, where('sessionActive', '==', true))
        : statusFilter === 'closed'
        ? query(base, where('sessionActive', '==', false))
        : query(base)
    );

    snap.docs.forEach((doc) => {
      const data = doc.data() as any;
      const when = parseDate(data.startTime);
      if (!when || when < start || when > end) return;

      const sessLotId = data.lotID || '';
      const lot = lotById[sessLotId];
      const vId = lot?.vendorId || '';

      if (partnerId !== 'all' && vId !== partnerId) return;
      if (lotId !== 'all' && sessLotId !== lotId) return;

      const amt = Number(data.amount) || 0;
      const v = data.vehicleInfo || {};
      const vehicle = [v.color, v.make, v.licensePlate].filter(Boolean).join(' ');
      const pname = partnerById[vId] || (vId ? vId : '—');
      const lotName = data.lotName || lot?.name || '—';

      recs.push({
        when,
        partnerName: pname,
        lotName,
        amount: amt,
        detail: {
          when,
          whenLabel: when.toLocaleString(),
          partner: pname,
          lot: lotName,
          customer: data.customerInfo?.email || '—',
          vehicle: vehicle || '—',
          minutes: Number(data.duration) || 0,
          amount: amt,
          method: data.gateway || '—',
          status: data.sessionActive ? 'Active' : 'Closed',
        },
      });
    });

    applyGroups(recs);
  }

  async function runCards() {
    const { start, end } = dateWindow();
    const selectedLotName = lotId === 'all' ? null : lotById[lotId]?.name ?? null;
    const recs: { when: Date; partnerName: string; lotName: string; amount: number; detail: CardRow }[] = [];

    if (lockedVendorId) {
      const rows = await callReport('cards');
      const pname = partnerById[lockedVendorId] || 'Your site';
      rows.forEach((r: any) => {
        const when = new Date(r.ts);
        if (when < start || when > end) return;
        if (selectedLotName && (r.lotName || '') !== selectedLotName) return;
        if (gatewayFilter !== 'all' && (r.gateway || '') !== gatewayFilter) return;
        const amt = Number(r.amount) || 0;
        const lotName = r.lotName || '—';
        recs.push({
          when,
          partnerName: pname,
          lotName,
          amount: amt,
          detail: {
            when,
            whenLabel: when.toLocaleString(),
            partner: pname,
            lot: lotName,
            customer: r.customer || '—',
            method: r.method || '—',
            gateway: r.gateway || '—',
            txnId: r.transactionId || '—',
            parkingFee: Number(r.parkingFee) || 0,
            salesTax: Number(r.salesTax) || 0,
            processingFee: Number(r.processingFee) || 0,
            amount: amt,
            currency: r.currency || 'USD',
          },
        });
      });
      applyGroups(recs);
      return;
    }

    const base = collection(db, 'payments');
    const snap = await getDocs(
      partnerId === 'all' ? query(base) : query(base, where('vendorId', '==', partnerId))
    );

    snap.docs.forEach((doc) => {
      const data = doc.data() as any;
      const when = parseDate(data.receiptTS);
      if (!when || when < start || when > end) return;
      if (selectedLotName && (data.lotName || '') !== selectedLotName) return;
      if (gatewayFilter !== 'all' && (data.gateway || '') !== gatewayFilter) return;

      const amt = Number(data.amount) || 0;
      const pname = partnerById[data.vendorId] || data.vendorId || '—';
      const lotName = data.lotName || '—';
      recs.push({
        when,
        partnerName: pname,
        lotName,
        amount: amt,
        detail: {
          when,
          whenLabel: when.toLocaleString(),
          partner: pname,
          lot: lotName,
          customer: data.customerInfo?.email || '—',
          method: data.paymentMethod || '—',
          gateway: data.gateway || '—',
          txnId: data.transactionId || '—',
          parkingFee: Number(data.parkingFee) || 0,
          salesTax: Number(data.salesTax) || 0,
          processingFee: Number(data.processingFee) || 0,
          amount: amt,
          currency: data.currency || 'USD',
        },
      });
    });

    applyGroups(recs);
  }

  async function handleRun() {
    setRunning(true);
    try {
      if (reportType === 'revenue') await runRevenue();
      else if (reportType === 'sessions') await runSessions();
      else await runCards();
      setHasRun(true);
    } catch (e) {
      console.error('Reports: run failed', e);
      alert('Could not run the report. See the console for details.');
    } finally {
      setRunning(false);
    }
  }

  function currentReport(): {
    title: string;
    base: string;
    headers: string[];
    rows: (string | number)[][];
  } {
    const partnerLabel = partnerId === 'all' ? 'all-partners' : (partnerById[partnerId] || partnerId);
    const range = `${fromDate} to ${toDate}`;
    const base = `${reportType}_${partnerLabel}_${fromDate}_to_${toDate}`.replace(/\s+/g, '-');
    const countLabel = reportType === 'cards' ? 'Transactions' : reportType === 'sessions' ? 'Sessions' : 'Payments';
    const title =
      (reportType === 'revenue' ? 'Revenue' : reportType === 'sessions' ? 'Sessions' : 'Card Transactions') +
      ` — ${partnerLabel} (${range})`;

    // Grouped export: Partner -> Location -> period, with a total line per
    // location, per partner, and a grand total. Same shape for every report so
    // partners are never mixed together.
    const headers = ['Partner', 'Location', 'Period', countLabel, 'Revenue'];
    const rows: (string | number)[][] = [];
    groups.forEach((pg) => {
      pg.locations.forEach((lg) => {
        lg.periods.forEach((per) => {
          rows.push([pg.partnerName, lg.lotName, per.period, per.count, per.total.toFixed(2)]);
        });
        rows.push([pg.partnerName, lg.lotName, 'Location total', lg.count, lg.total.toFixed(2)]);
      });
      rows.push([pg.partnerName, 'PARTNER TOTAL', '', pg.count, pg.total.toFixed(2)]);
    });
    rows.push(['GRAND TOTAL', '', '', grandCount, grandTotal.toFixed(2)]);
    return { title, base, headers, rows };
  }

  // Per-transaction / per-session line items, kept grouped by Partner and
  // Location so each row still says which site it belongs to. Revenue has no
  // line items of its own (it aggregates payments — use the Card report for the
  // individual charges), so it returns null.
  function detailExport(): { headers: string[]; rows: (string | number)[][] } | null {
    if (reportType === 'sessions') {
      const rows: (string | number)[][] = [];
      groups.forEach((pg) =>
        pg.locations.forEach((lg) =>
          (lg.detail as SessionRow[]).forEach((r) =>
            rows.push([
              pg.partnerName, lg.lotName, r.whenLabel, r.customer, r.vehicle,
              r.minutes, r.amount.toFixed(2), r.method, r.status,
            ])
          )
        )
      );
      return {
        headers: ['Partner', 'Location', 'When', 'Customer', 'Vehicle', 'Minutes', 'Amount', 'Method', 'Status'],
        rows,
      };
    }
    if (reportType === 'cards') {
      const rows: (string | number)[][] = [];
      groups.forEach((pg) =>
        pg.locations.forEach((lg) =>
          (lg.detail as CardRow[]).forEach((r) =>
            rows.push([
              pg.partnerName, lg.lotName, r.whenLabel, r.customer, r.method, r.gateway, r.txnId,
              r.parkingFee.toFixed(2), r.salesTax.toFixed(2), r.processingFee.toFixed(2),
              r.amount.toFixed(2), r.currency,
            ])
          )
        )
      );
      return {
        headers: ['Partner', 'Location', 'When', 'Customer', 'Method', 'Gateway', 'TransactionId',
          'ParkingFee', 'SalesTax', 'ProcessingFee', 'Total', 'Currency'],
        rows,
      };
    }
    return null;
  }

  const noRows = groups.length === 0;
  const doCsv = () => {
    const r = currentReport();
    const det = detailExport();
    if (det && det.rows.length > 0) {
      exportCsvSections(`${r.base}.csv`, [
        { title: 'Summary', headers: r.headers, rows: r.rows },
        { title: 'Line Items', headers: det.headers, rows: det.rows },
      ]);
    } else {
      exportCsv(`${r.base}.csv`, r.headers, r.rows);
    }
  };
  const doXlsx = () => {
    const r = currentReport();
    const det = detailExport();
    if (det && det.rows.length > 0) {
      exportXlsxSheets(`${r.base}.xlsx`, [
        { name: 'Summary', headers: r.headers, rows: r.rows },
        { name: 'Line Items', headers: det.headers, rows: det.rows },
      ]);
    } else {
      exportXlsx(`${r.base}.xlsx`, r.headers, r.rows);
    }
  };
  const doPdf = async () => {
    const r = currentReport();
    let chart: ChartImage | undefined;
    const svg = document.querySelector('svg[aria-label="Bar chart"]') as SVGSVGElement | null;
    if (svg) {
      try {
        chart = await svgToPng(svg);
      } catch {
        chart = undefined;
      }
    }
    exportPdf(r.title, `${r.base}.pdf`, r.headers, r.rows, chart);
  };
  const doPrint = () => window.print();

  const cardTotals = useMemo(() => {
    let fees = 0;
    let tax = 0;
    groups.forEach((pg) =>
      pg.locations.forEach((lg) =>
        lg.detail.forEach((d: any) => {
          fees += Number(d.processingFee) || 0;
          tax += Number(d.salesTax) || 0;
        })
      )
    );
    return { fees, tax };
  }, [groups]);

  // Location totals across every partner — feeds the summary chart and gives
  // the PDF a graphic. Labelled "Partner · Location" so sites stay distinct.
  const locationTotals = useMemo(() => {
    const out: { label: string; value: number }[] = [];
    groups.forEach((pg) =>
      pg.locations.forEach((lg) =>
        out.push({ label: `${pg.partnerName} · ${lg.lotName}`, value: lg.total })
      )
    );
    return out;
  }, [groups]);

  if (refLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        {fixedType === 'revenue'
          ? 'Revenue Report'
          : fixedType === 'sessions'
          ? 'Sessions Report'
          : fixedType === 'cards'
          ? 'Card Transactions'
          : 'Reports'}
      </Typography>
      <Typography color="textSecondary" sx={{ mb: 2 }}>
        {lockedVendorId
          ? 'Your locations, by date range. Use the quick ranges to see what is happening now.'
          : 'Run a report by partner, location, and date range.'}
      </Typography>

      {hasRun && (
        <Typography variant="body2" sx={{ mb: 2 }}>
          <strong>
            {lockedVendorId
              ? 'Your locations'
              : partnerId === 'all'
              ? 'All partners'
              : partnerById[partnerId] || partnerId}
          </strong>
          {' · '}
          {fromDate} → {toDate} · grouped by {groupBy}
        </Typography>
      )}

      {/* Filter bar */}
      <Paper sx={{ p: 2, mb: 3 }} className="no-print">
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          {!fixedType && (
            <TextField
              select
              label="Report"
              value={reportType}
              onChange={(e) => setReportType(e.target.value as 'revenue' | 'sessions' | 'cards')}
              sx={{ minWidth: 160 }}
              size="small"
            >
              <MenuItem value="revenue">Revenue</MenuItem>
              <MenuItem value="sessions">Sessions</MenuItem>
              <MenuItem value="cards">Credit Card</MenuItem>
            </TextField>
          )}

          {!lockedVendorId && (
            <TextField
              select
              label="Partner"
              value={partnerId}
              onChange={(e) => setPartnerId(e.target.value)}
              sx={{ minWidth: 200 }}
              size="small"
            >
              <MenuItem value="all">All partners</MenuItem>
              {partners.map((p) => (
                <MenuItem key={p.docId} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </TextField>
          )}

          <TextField
            select
            label="Location"
            value={lotId}
            onChange={(e) => setLotId(e.target.value)}
            sx={{ minWidth: 220 }}
            size="small"
          >
            <MenuItem value="all">All locations</MenuItem>
            {lotsForPartner.map((l) => (
              <MenuItem key={l.id} value={l.id}>
                {l.name}
                {l.code ? ` (${l.code})` : ''}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Group by"
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value as any)}
            sx={{ minWidth: 130 }}
            size="small"
          >
            <MenuItem value="day">Day</MenuItem>
            <MenuItem value="week">Week</MenuItem>
            <MenuItem value="month">Month</MenuItem>
            <MenuItem value="year">Year</MenuItem>
          </TextField>

          {reportType === 'sessions' && (
            <TextField
              select
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              sx={{ minWidth: 130 }}
              size="small"
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="closed">Closed</MenuItem>
            </TextField>
          )}

          {reportType === 'cards' && (
            <TextField
              select
              label="Gateway"
              value={gatewayFilter}
              onChange={(e) => setGatewayFilter(e.target.value as any)}
              sx={{ minWidth: 130 }}
              size="small"
            >
              <MenuItem value="all">All gateways</MenuItem>
              <MenuItem value="nmi">NMI</MenuItem>
              <MenuItem value="stripe">Stripe</MenuItem>
            </TextField>
          )}

          <TextField
            label="From"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="To"
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
          />

          <Button variant="contained" onClick={handleRun} disabled={running}>
            {running ? 'Running…' : 'Run report'}
          </Button>
          <Button variant="outlined" onClick={doPrint} disabled={!hasRun}>
            Print
          </Button>
          <Button variant="outlined" onClick={doPdf} disabled={!hasRun || noRows}>
            PDF
          </Button>
          <Button variant="outlined" onClick={doXlsx} disabled={!hasRun || noRows}>
            Excel
          </Button>
          <Button variant="outlined" onClick={doCsv} disabled={!hasRun || noRows}>
            CSV
          </Button>
        </Box>

        {/* Quick date ranges */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 2 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mr: 0.5 }}>
            Quick range:
          </Typography>
          {PRESETS.map((p) => (
            <Chip
              key={p.key}
              label={p.label}
              size="small"
              variant="outlined"
              onClick={() => applyPreset(p.key)}
            />
          ))}
        </Box>
      </Paper>

      {!hasRun && (
        <Typography color="textSecondary">
          Choose your filters and press <strong>Run report</strong>.
        </Typography>
      )}

      {hasRun && (
        <>
          {/* Summary */}
          <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
            <StatCard
              label={reportType === 'cards' ? 'Total Charged' : 'Total Revenue'}
              value={money(grandTotal)}
            />
            <StatCard
              label={reportType === 'revenue' ? 'Payments' : reportType === 'cards' ? 'Transactions' : 'Sessions'}
              value={String(grandCount)}
            />
            {reportType === 'cards' && (
              <>
                <StatCard label="Processing Fees" value={money(cardTotals.fees)} />
                <StatCard label="Sales Tax" value={money(cardTotals.tax)} />
              </>
            )}
          </Box>

          {/* Revenue by location — one bar per site, across all partners */}
          {locationTotals.length > 0 && (
            <>
              <Typography variant="h6" gutterBottom>
                Revenue by Location
              </Typography>
              <Paper sx={{ p: 2, mb: 3 }}>
                <BarChart data={locationTotals} valuePrefix="$" />
              </Paper>
            </>
          )}

          {groups.length === 0 && (
            <Typography color="textSecondary">No data in this range.</Typography>
          )}

          {/* Partner -> Location -> period breakdown. Partners never mixed. */}
          {groups.map((pg) => (
            <Box
              key={pg.partnerName}
              sx={{ mb: 4, '@media print': { breakInside: 'avoid' } }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  flexWrap: 'wrap',
                  gap: 1,
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  px: 2,
                  py: 1,
                  borderRadius: 1,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {pg.partnerName}
                </Typography>
                <Typography variant="body2">
                  {money(pg.total)} · {pg.count}{' '}
                  {reportType === 'revenue' ? 'payments' : reportType === 'cards' ? 'transactions' : 'sessions'}
                </Typography>
              </Box>

              {pg.locations.map((lg) => {
                const maxP = lg.periods.reduce((m, p) => Math.max(m, p.total), 0);
                return (
                  <Paper
                    key={lg.lotName}
                    sx={{ p: 2, mt: 2, ml: { sm: 2 }, '@media print': { breakInside: 'avoid' } }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                        flexWrap: 'wrap',
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {lg.lotName}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Location total: {money(lg.total)} · {lg.count}{' '}
                        {reportType === 'revenue' ? 'payments' : reportType === 'cards' ? 'transactions' : 'sessions'}
                      </Typography>
                    </Box>

                    {/* Period breakdown for THIS location only */}
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>
                              {groupBy.charAt(0).toUpperCase() + groupBy.slice(1)}
                            </TableCell>
                            <TableCell align="right">
                              {reportType === 'revenue' ? 'Payments' : reportType === 'cards' ? 'Transactions' : 'Sessions'}
                            </TableCell>
                            <TableCell align="right">Revenue</TableCell>
                            <TableCell sx={{ width: '35%' }}></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {lg.periods.map((per) => (
                            <TableRow key={per.period}>
                              <TableCell>{per.period}</TableCell>
                              <TableCell align="right">{per.count}</TableCell>
                              <TableCell align="right">{money(per.total)}</TableCell>
                              <TableCell>
                                <Box
                                  sx={{
                                    height: 10,
                                    borderRadius: 1,
                                    bgcolor: 'primary.main',
                                    width: `${maxP > 0 ? (per.total / maxP) * 100 : 0}%`,
                                    minWidth: per.total > 0 ? 2 : 0,
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>{lg.count}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>{money(lg.total)}</TableCell>
                            <TableCell />
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {/* Session line items for this location */}
                    {reportType === 'sessions' && lg.detail.length > 0 && (
                      <>
                        <Divider sx={{ my: 1.5 }} />
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>When</TableCell>
                                <TableCell>Customer</TableCell>
                                <TableCell>Vehicle</TableCell>
                                <TableCell align="right">Min</TableCell>
                                <TableCell align="right">Amount</TableCell>
                                <TableCell>Method</TableCell>
                                <TableCell>Status</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {lg.detail.slice(0, 500).map((r: SessionRow, i: number) => (
                                <TableRow key={i}>
                                  <TableCell>{r.whenLabel}</TableCell>
                                  <TableCell>{r.customer}</TableCell>
                                  <TableCell>{r.vehicle}</TableCell>
                                  <TableCell align="right">{r.minutes}</TableCell>
                                  <TableCell align="right">{money(r.amount)}</TableCell>
                                  <TableCell>{r.method}</TableCell>
                                  <TableCell>
                                    <Chip
                                      size="small"
                                      label={r.status}
                                      color={r.status === 'Active' ? 'success' : 'default'}
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        {lg.detail.length > 500 && (
                          <Typography color="textSecondary" variant="caption" sx={{ mt: 1, display: 'block' }}>
                            Showing the first 500 rows on screen — CSV/Excel include all {lg.detail.length}.
                          </Typography>
                        )}
                      </>
                    )}

                    {/* Card line items for this location */}
                    {reportType === 'cards' && lg.detail.length > 0 && (
                      <>
                        <Divider sx={{ my: 1.5 }} />
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>When</TableCell>
                                <TableCell>Customer</TableCell>
                                <TableCell>Method</TableCell>
                                <TableCell>Gateway</TableCell>
                                <TableCell>Transaction ID</TableCell>
                                <TableCell align="right">Parking</TableCell>
                                <TableCell align="right">Tax</TableCell>
                                <TableCell align="right">Fee</TableCell>
                                <TableCell align="right">Total</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {lg.detail.slice(0, 500).map((r: CardRow, i: number) => (
                                <TableRow key={i}>
                                  <TableCell>{r.whenLabel}</TableCell>
                                  <TableCell>{r.customer}</TableCell>
                                  <TableCell>{r.method}</TableCell>
                                  <TableCell>
                                    <Chip size="small" label={r.gateway} />
                                  </TableCell>
                                  <TableCell>{r.txnId}</TableCell>
                                  <TableCell align="right">{money(r.parkingFee)}</TableCell>
                                  <TableCell align="right">{money(r.salesTax)}</TableCell>
                                  <TableCell align="right">{money(r.processingFee)}</TableCell>
                                  <TableCell align="right">{money(r.amount)}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        {lg.detail.length > 500 && (
                          <Typography color="textSecondary" variant="caption" sx={{ mt: 1, display: 'block' }}>
                            Showing the first 500 rows on screen — CSV/Excel include all {lg.detail.length}.
                          </Typography>
                        )}
                      </>
                    )}
                  </Paper>
                );
              })}
            </Box>
          ))}

          {/* Grand total across every partner */}
          {groups.length > 0 && (
            <Divider sx={{ my: 2 }} />
          )}
          {groups.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 3, mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Grand total: {money(grandTotal)} · {grandCount}{' '}
                {reportType === 'revenue' ? 'payments' : reportType === 'cards' ? 'transactions' : 'sessions'}
              </Typography>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
